import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { Icon } from '@blueprintjs/core';
import { Button } from 'components/buttons';
import { DateToLocal } from 'components/dates';
import { FormHelperText, FormSelect } from 'components/form-fields';
import { ShowHide } from 'components/layout';
import { Table } from 'components/listing';
import { Tooltip } from 'components/tooltip';
import {
    buildNewTrigger,
    changeTriggerType,
    getTriggerErrorMessages,
    getTriggerTypesForTopics,
    isTriggerSaved
} from './reviewTriggerHelpers';
import { getReviewTriggerType } from './triggers';
import './EmployeeReviewsShared.css';

const defaultObject = {};
const defaultArray = [];
const defaultFunction = () => { };

// The table of review triggers, editable or read-only, used wherever a review type's or a
// driver's triggers are shown. Row and control ids start with idPrefix so that more than one
// table can sit on a page. The caller supplies the heading and helper text. A read-only table with
// no triggers shows emptyMessage in place of the table. Tables given the same columnWidths line up
// when one sits above another.
export function ReviewTriggersTable(
    {
        triggers = defaultArray,
        onChange = defaultFunction,
        topicIds = defaultArray,
        errors = defaultObject,
        reviewOptions = defaultObject,
        heading = null,
        helperText = null,
        emptyMessage = null,
        idPrefix = "review-type",
        loading = false,
        saving = false,
        disabled = false,
        columnWidths = defaultArray,
        createMode = false
    }
) {

    const offeredTriggerTypes = getTriggerTypesForTopics(topicIds, reviewOptions);
    const controlsDisabled = saving || disabled;

    const triggerTypeItems = offeredTriggerTypes.map((triggerType) => {
        return {
            id: triggerType.key,
            name: triggerType.name
        };
    });

    const headers = [
        { key: "trigger", value: "Trigger" },
        { key: "condition", value: "Condition" }
    ];

    // A read-only table keeps an empty Action column so its columns line up with an editable one.
    headers.push({ key: "action", value: disabled ? "" : "Action" });

    function getTriggerId(index) {
        return `${idPrefix}-trigger-${index}`;
    }

    function findOfferedTriggerType(triggerKey) {
        return offeredTriggerTypes.find((triggerType) => triggerType.key === triggerKey);
    }

    function onAddTriggerSelect(item) {
        onChange([...triggers, buildNewTrigger(findOfferedTriggerType(item.id))]);
    }

    function onRemoveTrigger(index) {
        onChange(triggers.filter((_, triggerIndex) => triggerIndex !== index));
    }

    function onTriggerTypeSelect(index, item) {
        onChange(triggers.map((trigger, triggerIndex) => {
            if (triggerIndex !== index) {
                return trigger;
            }

            return changeTriggerType(trigger, findOfferedTriggerType(item.id));
        }));
    }

    function onConditionChange(index, condition) {
        onChange(triggers.map((trigger, triggerIndex) => {
            if (triggerIndex !== index) {
                return trigger;
            }

            return {
                ...trigger,
                condition: condition
            };
        }));
    }

    // A trigger only counts infringements raised from when it was saved, so the table says when that
    // was for one added after the review type was created, and warns while a change is unsaved. A
    // review type being created has nothing saved to compare against.
    function getTriggerNotice(trigger) {
        if (createMode) {
            return null;
        }

        if (!isTriggerSaved(trigger)) {
            return (
                <span>Not saved yet. Infringements raised before it is saved won't count towards it.</span>
            );
        }

        if (trigger.addedWithReviewType === false && trigger.createdDate != null) {
            return (
                <span>Added or last changed on <DateToLocal format="DD MMM YYYY">{trigger.createdDate}</DateToLocal>. Infringements raised before this date don't count towards it, even if they arrived after an employee's last review.</span>
            );
        }

        return null;
    }

    function formatTriggerRow(trigger, index) {
        const cells = [
            (
                <FormSelect
                    key={`${trigger.clientId}-type-cell`}
                    id={`${getTriggerId(index)}-type`}
                    items={triggerTypeItems}
                    placeholder="Please select"
                    loading={loading}
                    disabled={controlsDisabled}
                    selectedValue={trigger.triggerKey}
                    onItemSelect={(item) => onTriggerTypeSelect(index, item)}
                />
            ),
            renderCondition(trigger, index)
        ];

        if (disabled) {
            cells.push(null);
        } else {
            cells.push(
                <Button
                    key={`${trigger.clientId}-remove-cell`}
                    id={`${getTriggerId(index)}-remove`}
                    icon="trash"
                    minimal
                    iconOnly
                    loading={loading}
                    disabled={saving}
                    onClick={() => onRemoveTrigger(index)}
                />
            );
        }

        return {
            key: trigger.clientId,
            cells: cells
        };
    }

    function renderCondition(trigger, index) {
        const ConditionEditor = getReviewTriggerType(trigger.triggerKey).ConditionEditor;
        const notice = getTriggerNotice(trigger);

        return (
            <div key={`${trigger.clientId}-condition-cell`} id={getTriggerId(index)} className="inline-items">
                <div className="inline-items">
                    <ConditionEditor
                        id={getTriggerId(index)}
                        condition={trigger.condition}
                        onChange={(condition) => onConditionChange(index, condition)}
                        reviewOptions={reviewOptions}
                        loading={loading}
                        disabled={controlsDisabled}
                    />
                </div>
                <ShowHide
                    evaluator={notice != null}
                    show={(
                        <div id={`${getTriggerId(index)}-notice`} className="inline-items review-trigger-notice">
                            <Tooltip content={notice}>
                                <Icon icon="info-sign" size={12} />
                            </Tooltip>
                        </div>
                    )}
                />
            </div>
        );
    }

    // The last row adds a trigger: choosing a type adds a row of that type, and the select
    // returns to its prompt because it never holds a value of its own.
    function buildAddTriggerRow() {
        return {
            key: `${idPrefix}-add-trigger-row`,
            cells: [
                (
                    <FormSelect
                        key={`${idPrefix}-add-trigger-cell`}
                        id={`${idPrefix}-add-trigger`}
                        items={triggerTypeItems}
                        placeholder="Please select to add a trigger"
                        loading={loading}
                        disabled={saving}
                        onItemSelect={onAddTriggerSelect}
                    />
                ),
                ...headers.slice(1).map(() => null)
            ]
        };
    }

    const rows = triggers.map(formatTriggerRow);

    if (!disabled) {
        rows.push(buildAddTriggerRow());
    }

    const errorMessages = getTriggerErrorMessages(errors, triggers.length);

    return (
        <div className="spacer-bottom review-triggers">
            <ShowHide
                evaluator={heading != null}
                show={(
                    <h3 className={classNames({ "bp6-skeleton": loading })}>{heading}</h3>
                )}
            />
            <ShowHide
                evaluator={helperText != null}
                show={(
                    <FormHelperText loading={loading}>{helperText}</FormHelperText>
                )}
            />
            <ShowHide
                evaluator={disabled && emptyMessage != null && triggers.length === 0}
                show={(
                    <p className={classNames("bp6-text-muted", { "bp6-skeleton": loading })}>{emptyMessage}</p>
                )}
                hide={(
                    <Table
                        data={rows}
                        headerData={{ headers: headers }}
                        columnWidths={columnWidths}
                        tableClasses={classNames({ "review-triggers-fixed-columns": columnWidths.length > 0 })}
                        loadingData={loading}
                        noWrap
                    />
                )}
            />
            {errorMessages.map((message) => {
                return (
                    <div key={message} className="form-row spacer-bottom-small">
                        <FormHelperText loading={loading} danger={true}>{message}</FormHelperText>
                    </div>
                );
            })}
        </div>
    );
}

ReviewTriggersTable.propTypes = {
    triggers: PropTypes.array,
    onChange: PropTypes.func,
    topicIds: PropTypes.arrayOf(PropTypes.number),
    errors: PropTypes.object,
    reviewOptions: PropTypes.object,
    heading: PropTypes.node,
    helperText: PropTypes.node,
    emptyMessage: PropTypes.node,
    idPrefix: PropTypes.string,
    loading: PropTypes.bool,
    saving: PropTypes.bool,
    disabled: PropTypes.bool,
    columnWidths: PropTypes.array,
    createMode: PropTypes.bool
};
